import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useDesks } from "@/hooks/use-desk-migration";

interface Props {
  tenantId: string;
  value: string;
  onChange: (deskId: string) => void;
  placeholder?: string;
  className?: string;
  "aria-label"?: string;
}

/** Searchable Harmony desk picker (value = desk id, label = desk name). */
export function DeskSelect({
  tenantId,
  value,
  onChange,
  placeholder = "Select a desk...",
  className,
  "aria-label": ariaLabel,
}: Props) {
  const [open, setOpen] = useState(false);
  const { data: desks, isLoading } = useDesks(tenantId);

  const sorted = useMemo(
    () => [...(desks ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [desks],
  );
  const selected = desks?.find((d) => d.id === value);

  return (
    // modal so the popover works when rendered inside a Dialog (otherwise
    // the Dialog's focus/pointer trap swallows item clicks).
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          className={cn("h-8 w-full justify-between font-normal", className)}
        >
          <span className={cn(!selected && "text-muted-foreground", "truncate")}>
            {selected ? selected.name : value || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0">
        <Command>
          <CommandInput placeholder="Search desks..." />
          <CommandList>
            <CommandEmpty>
              {isLoading ? "Loading..." : "No desk found."}
            </CommandEmpty>
            <CommandGroup>
              {sorted.map((desk) => (
                <CommandItem
                  key={desk.id}
                  value={`${desk.name} ${desk.id}`}
                  onSelect={() => {
                    onChange(desk.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === desk.id ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="truncate">{desk.name}</span>
                  <span className="text-muted-foreground ml-2 truncate text-xs">
                    {desk.id}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
